<!-- Reusable AND/OR filter builder over image columns (name + case-info), shared by
     the project Images page and the viewer Images drawer. v-model is the filters
     array ({id, column, operator, value, conj}); `groups` is the filterable column
     groups. Produces no query itself — utils/image-search.js turns the rows into a
     filterExpression. -->
<template>
<div class="fb">
  <div v-if="!value.length" class="fb-empty">{{ $t('no-filters-yet') }}</div>
  <div v-for="(f, i) in value" :key="f.id" class="fb-row">
    <button class="fb-x" @click="remove(i)" :title="$t('button-delete')"><i class="fas fa-times"></i></button>
    <span v-if="i === 0" class="fb-first"></span>
    <div v-else class="fb-conj">
      <select :value="f.conj" @change="e => update(i, {conj: e.target.value})">
        <option value="and">{{ $t('and') }}</option>
        <option value="or">{{ $t('or') }}</option>
      </select>
      <i class="fas fa-chevron-down fb-conj-chev"></i>
    </div>

    <div class="fb-field">
      <div class="fb-label">{{ $t('columns') }}</div>
      <div class="fb-select">
        <select :value="f.column" @change="e => onColumn(i, e.target.value)">
          <optgroup v-for="g in groups" :key="g.id" :label="$t(g.labelKey)">
            <option v-for="c in g.cols" :key="c.key" :value="c.key">{{ $t(c.labelKey) }}</option>
          </optgroup>
        </select>
        <i class="fas fa-chevron-down fb-select-chev"></i>
      </div>
    </div>

    <div class="fb-field">
      <div class="fb-label">{{ $t('operator') }}</div>
      <div class="fb-select">
        <select :value="f.operator" @change="e => update(i, {operator: e.target.value})">
          <option v-for="op in opsFor(colType(f.column))" :key="op" :value="op">{{ $t(opLabelKey(op)) }}</option>
        </select>
        <i class="fas fa-chevron-down fb-select-chev"></i>
      </div>
    </div>

    <div class="fb-field">
      <div class="fb-label">{{ $t('value') }}</div>
      <input
        v-if="f.operator !== 'is-empty'"
        class="fb-value"
        :type="valueType(f.column)"
        :value="f.value"
        :placeholder="$t('enter-value')"
        @input="e => update(i, {value: e.target.value})"
      />
      <div v-else class="fb-value fb-value-empty">—</div>
    </div>
  </div>

  <div class="fb-actions">
    <button class="fb-add" @click="add"><i class="fas fa-plus"></i> {{ $t('add-filter') }}</button>
    <button class="fb-removeall" @click="$emit('input', [])"><i class="fas fa-trash"></i> {{ $t('remove-all') }}</button>
  </div>
</div>
</template>

<script>
import {COLUMNS, COLUMN_BY_KEY, opsFor, OP_LABEL_KEY, makeFilter} from '@/utils/image-table-columns';

export default {
  name: 'image-filter-builder',
  props: {
    value: {type: Array, default: () => []},
    groups: {type: Array, default: () => []}
  },
  methods: {
    opsFor,
    opLabelKey(op) {
      return OP_LABEL_KEY[op] || op;
    },
    colType(key) {
      const c = COLUMN_BY_KEY[key];
      return c ? c.type : 'text';
    },
    valueType(key) {
      const t = this.colType(key);
      return t === 'number' ? 'number' : (t === 'date' ? 'date' : 'text');
    },
    add() {
      const first = COLUMNS.find(c => c.filterable);
      this.$emit('input', [...this.value, makeFilter(first.key, 'and')]);
    },
    remove(i) {
      const next = this.value.slice();
      next.splice(i, 1);
      this.$emit('input', next);
    },
    update(i, patch) {
      const next = this.value.slice();
      next[i] = {...next[i], ...patch};
      this.$emit('input', next);
    },
    onColumn(i, key) {
      const c = COLUMN_BY_KEY[key];
      this.update(i, {column: key, operator: opsFor(c.type)[0], value: ''});
    }
  }
};
</script>

<style scoped>
.fb-empty { color: #6b7280; font-size: 14px; padding: 4px 2px 14px; }
.fb-row { display: flex; align-items: flex-end; gap: 14px; margin-bottom: 14px; }
.fb-x { background: transparent; border: 0; color: #8b94a3; cursor: pointer; padding: 4px; margin-bottom: 6px; }
.fb-x:hover { color: #c8443c; }
.fb-first { width: 30px; flex-shrink: 0; }
.fb-conj { position: relative; width: 70px; flex-shrink: 0; }
.fb-conj select {
  appearance: none; -webkit-appearance: none;
  border: 0; border-bottom: 1px solid #d7dbe3; background: transparent;
  font-size: 14px; font-weight: 600; color: #3a4452;
  padding: 8px 18px 8px 2px; width: 100%; cursor: pointer; outline: none;
}
.fb-conj-chev { position: absolute; right: 2px; bottom: 11px; color: #9aa3b2; pointer-events: none; font-size: 0.7rem; }
.fb-field { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 120px; }
.fb-label { font-size: 10.5px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #8b94a3; }
.fb-select { position: relative; }
.fb-select select {
  appearance: none; -webkit-appearance: none;
  border: 0; border-bottom: 1px solid #d7dbe3; background: transparent;
  font-size: 14px; color: #1f2937; padding: 8px 24px 8px 2px; width: 100%; cursor: pointer; outline: none; font-weight: 600;
}
.fb-select-chev { position: absolute; right: 4px; top: 9px; color: #9aa3b2; pointer-events: none; font-size: 0.8rem; }
.fb-value { border: 0; border-bottom: 1px solid #d7dbe3; background: transparent; font-size: 14px; color: #1f2937; padding: 8px 2px; width: 100%; outline: none; }
.fb-value:focus { border-bottom-color: #1e63d0; }
.fb-value-empty { color: #9aa3b2; }
.fb-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 6px; padding-top: 14px; border-top: 1px dashed #e8eaef; }
.fb-add {
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1px solid #e8eaef; border-radius: 10px;
  padding: 9px 15px; font-size: 14px; font-weight: 600; color: #3a4452; cursor: pointer;
}
.fb-add:hover { background: #f8fafc; }
.fb-removeall {
  display: flex; align-items: center; gap: 8px;
  background: #27324a; border: 0; border-radius: 10px;
  padding: 9px 15px; font-size: 14px; font-weight: 600; color: #fff; cursor: pointer;
}
</style>
